/**
 * textures.js — Procedural terrain texture generator (pixel-art style)
 * Creates 32×32 canvas patterns for each terrain type using only integer-coord
 * fillRects, hard palettes, no curves, no alpha washes. Patterns cache after
 * first generation. Matches the chunky sprite aesthetic used by character /
 * monster tokens.
 */

const CACHE = {};

// ── Helpers ───────────────────────────────────────────────────────────────

function make(size, fn) {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  fn(ctx, size);
  return canvas;
}

/** Deterministic pseudo-random stream from a seed. */
function rng(seed) {
  let s = seed | 0;
  return () => {
    s = Math.imul(s ^ (s >>> 15), s | 1);
    s ^= s + Math.imul(s ^ (s >>> 7), s | 61);
    return ((s ^ (s >>> 14)) >>> 0) / 0x100000000;
  };
}

/** Scatter `count` axis-aligned fillRects of the given color & size. */
function scatter(ctx, r, S, color, count, w = 1, h = 1) {
  ctx.fillStyle = color;
  const maxX = S - w, maxY = S - h;
  for (let i = 0; i < count; i++) {
    const x = Math.floor(r() * (maxX + 1));
    const y = Math.floor(r() * (maxY + 1));
    ctx.fillRect(x, y, w, h);
  }
}

/** Fill base color, solid. */
function base(ctx, S, color) {
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, S, S);
}

// ── Terrain generators ─────────────────────────────────────────────────────

const GENERATORS = {

  stone_floor: () => make(32, (ctx, S) => {
    base(ctx, S, '#5a5a72');
    const r = rng(1);
    scatter(ctx, r, S, '#3f3f55', 4, 2, 2);
    scatter(ctx, r, S, '#4a4a60', 10);
    scatter(ctx, r, S, '#6a6a82', 6);
    scatter(ctx, r, S, '#2e2e42', 3, 1, 2); // crack bits
  }),

  wall: () => make(32, (ctx, S) => {
    // Stone wall — blocky brick pattern. Bricks are mid-gray so the pattern
    // reads clearly and doesn't collapse into a flat dark shape.
    base(ctx, S, '#141420'); // dark mortar
    const r = rng(2);
    const brickH = 8, brickW = 15, gap = 1;
    const palette = ['#5a5a70', '#4e4e64', '#646478', '#545468'];
    for (let row = 0; row < 4; row++) {
      const offset = (row % 2) * Math.floor((brickW + gap) / 2);
      for (let col = -1; col < 4; col++) {
        const bx = col * (brickW + gap) + offset;
        const by = row * (brickH + gap);
        ctx.fillStyle = palette[Math.floor(r() * palette.length)];
        ctx.fillRect(bx, by, brickW, brickH);
        // Top highlight
        ctx.fillStyle = '#7e7e92';
        ctx.fillRect(bx, by, brickW, 1);
        // Bottom shadow
        ctx.fillStyle = '#2a2a3e';
        ctx.fillRect(bx, by + brickH - 1, brickW, 1);
      }
    }
  }),

  dirt: () => make(32, (ctx, S) => {
    base(ctx, S, '#7a5c2a');
    const r = rng(3);
    scatter(ctx, r, S, '#523d18', 12);
    scatter(ctx, r, S, '#8a6c3a', 8);
    scatter(ctx, r, S, '#a08040', 5);
    scatter(ctx, r, S, '#3a2a10', 3, 2, 2); // pebbles
    scatter(ctx, r, S, '#5a4220', 2, 2, 1); // long clods
  }),

  grass: () => make(32, (ctx, S) => {
    base(ctx, S, '#3d7a30');
    const r = rng(4);
    scatter(ctx, r, S, '#2a5020', 5, 2, 2);
    scatter(ctx, r, S, '#326428', 7);
    scatter(ctx, r, S, '#4e9040', 4);
    // Grass blades — 1×2 verticals
    ctx.fillStyle = '#4a8838';
    for (let i = 0; i < 4; i++) {
      ctx.fillRect(Math.floor(r() * S), Math.floor(r() * (S - 2)), 1, 2);
    }
    scatter(ctx, r, S, '#1e3818', 3, 1, 2);
  }),

  water: () => make(32, (ctx, S) => {
    base(ctx, S, '#1a4a8a');
    const r = rng(5);
    scatter(ctx, r, S, '#0e2e5a', 5, 2, 2);
    scatter(ctx, r, S, '#2a5a9a', 10);
    scatter(ctx, r, S, '#3a6aaa', 7);
    // Surface foam — short 2×1 streaks
    ctx.fillStyle = '#8abaee';
    for (let i = 0; i < 3; i++) {
      ctx.fillRect(Math.floor(r() * (S - 2)), Math.floor(r() * S), 2, 1);
    }
    scatter(ctx, r, S, '#5a9acc', 5);
  }),

  shallow_water: () => make(32, (ctx, S) => {
    base(ctx, S, '#3a7abf');
    const r = rng(51);
    scatter(ctx, r, S, '#2a5a9a', 4, 2, 2);
    scatter(ctx, r, S, '#5a9acc', 10);
    scatter(ctx, r, S, '#a08050', 3); // sandy bottom hints
    scatter(ctx, r, S, '#a0d0ff', 5); // shimmer
    ctx.fillStyle = '#c0e0ff';
    for (let i = 0; i < 2; i++) {
      ctx.fillRect(Math.floor(r() * (S - 3)), Math.floor(r() * S), 3, 1);
    }
  }),

  // Deep but passable — for swim speed creatures. Darker than shallow_water,
  // more wave streaks, no sandy-bottom hints (it's deep).
  swim_water: () => make(32, (ctx, S) => {
    base(ctx, S, '#2a5a9a');
    const r = rng(52);
    scatter(ctx, r, S, '#1a3a7a', 6, 2, 2);
    scatter(ctx, r, S, '#3a6aaa', 8);
    scatter(ctx, r, S, '#4a7aba', 6);
    // Long wave streaks
    ctx.fillStyle = '#7aaaee';
    for (let i = 0; i < 4; i++) {
      ctx.fillRect(Math.floor(r() * (S - 5)), Math.floor(r() * S), 5, 1);
    }
    ctx.fillStyle = '#a0c8ee';
    for (let i = 0; i < 2; i++) {
      ctx.fillRect(Math.floor(r() * (S - 3)), Math.floor(r() * S), 3, 1);
    }
  }),

  // Rocky cliff face — for climb speed creatures. Stronger vertical striations
  // to read as "wall you can scale" vs ground rock.
  climb_rock: () => make(32, (ctx, S) => {
    base(ctx, S, '#6a5a4a');
    const r = rng(53);
    scatter(ctx, r, S, '#4a3a2a', 6, 2, 3);
    scatter(ctx, r, S, '#7a6a5a', 10);
    scatter(ctx, r, S, '#8a7a6a', 5);
    scatter(ctx, r, S, '#3a2a1a', 4, 1, 3);
    // Vertical striations — handholds/cracks
    ctx.fillStyle = '#2a1e10';
    for (let i = 0; i < 4; i++) {
      const x = Math.floor(r() * S);
      const y = Math.floor(r() * (S - 6));
      ctx.fillRect(x, y, 1, 4 + Math.floor(r() * 4));
    }
    // Highlight bumps
    ctx.fillStyle = '#a08c70';
    scatter(ctx, r, S, '#a08c70', 4, 1, 1);
  }),

  lava: () => make(32, (ctx, S) => {
    base(ctx, S, '#1a0500');
    const r = rng(6);
    scatter(ctx, r, S, '#3a0a00', 4, 2, 2);
    scatter(ctx, r, S, '#5a1a00', 6);
    scatter(ctx, r, S, '#ff4400', 6);
    scatter(ctx, r, S, '#ff8800', 5);
    scatter(ctx, r, S, '#ffcc22', 3);
    // Glowing cracks — 1×2 and 2×1 streaks
    ctx.fillStyle = '#ff6600';
    for (let i = 0; i < 3; i++) {
      ctx.fillRect(Math.floor(r() * (S - 3)), Math.floor(r() * S), 3, 1);
    }
  }),

  wood_floor: () => make(32, (ctx, S) => {
    // Two planks split at x=16. Minimal grain so the plank shape reads.
    base(ctx, S, '#7a4e2c');
    const r = rng(7);
    ctx.fillStyle = '#6b4226';
    ctx.fillRect(16, 0, 16, S);
    // One grain streak per plank
    ctx.fillStyle = '#5a3820';
    ctx.fillRect(2, 6, 12, 1);
    ctx.fillRect(18, 20, 12, 1);
    ctx.fillStyle = '#8a5a32';
    ctx.fillRect(4, 18, 8, 1);
    ctx.fillRect(20, 10, 8, 1);
    // Plank seam (vertical) + horizontal divider halfway
    ctx.fillStyle = '#2a1808';
    ctx.fillRect(15, 0, 1, S);
    ctx.fillRect(0, 15, S, 1);
    // Single small knot
    if (r() > 0.5) {
      const kx = Math.floor(r() * 12) + 2;
      const ky = Math.floor(r() * 12) + 18;
      ctx.fillStyle = '#3a200c';
      ctx.fillRect(kx, ky, 2, 2);
    }
  }),

  sand: () => make(32, (ctx, S) => {
    base(ctx, S, '#c2a050');
    const r = rng(8);
    scatter(ctx, r, S, '#9a7c30', 10);
    scatter(ctx, r, S, '#b2905a', 12);
    scatter(ctx, r, S, '#d2b070', 8);
    scatter(ctx, r, S, '#6a5218', 3);
    // Subtle ripple — horizontal 1×1 streaks
    ctx.fillStyle = '#a08440';
    for (let i = 0; i < 3; i++) {
      const y = Math.floor(r() * S);
      for (let x = Math.floor(r() * 8); x < S; x += 4 + Math.floor(r() * 3)) {
        ctx.fillRect(x, y, 2, 1);
      }
    }
  }),

  snow: () => make(32, (ctx, S) => {
    base(ctx, S, '#c8dde8');
    const r = rng(9);
    scatter(ctx, r, S, '#a8c0d0', 5, 2, 2);
    scatter(ctx, r, S, '#b8cfdc', 10);
    scatter(ctx, r, S, '#ffffff', 10);
    scatter(ctx, r, S, '#e0ecf2', 8);
  }),

  deep_snow: () => make(32, (ctx, S) => {
    // Darker, bluer base than regular snow + a couple of drift-ridge bands.
    base(ctx, S, '#a8c4dc');
    const r = rng(91);
    scatter(ctx, r, S, '#7a9ab8', 7, 2, 2);
    scatter(ctx, r, S, '#8ea8c4', 10);
    scatter(ctx, r, S, '#c8dde8', 8);
    scatter(ctx, r, S, '#ffffff', 6);
    // Drift ridges — 2px horizontal bands with a bright crown
    const ridges = [6, 18, 27];
    for (const y of ridges) {
      ctx.fillStyle = '#6a86a0';
      ctx.fillRect(0, y + 1, S, 1);
      ctx.fillStyle = '#e8f0f8';
      ctx.fillRect(0, y, S, 1);
    }
    // Deep shadow pits
    scatter(ctx, r, S, '#506678', 5, 2, 1);
  }),

  ice: () => make(32, (ctx, S) => {
    base(ctx, S, '#90b8d0');
    const r = rng(11);
    scatter(ctx, r, S, '#70a0c0', 4, 2, 2);
    scatter(ctx, r, S, '#b0d8f0', 8);
    scatter(ctx, r, S, '#ffffff', 4);
    // Angular crack lines — straight 1-px diagonals
    ctx.fillStyle = '#4a7898';
    for (let i = 0; i < 3; i++) {
      let x = Math.floor(r() * S);
      let y = Math.floor(r() * S);
      const dx = r() > 0.5 ? 1 : -1;
      const dy = r() > 0.5 ? 1 : -1;
      const len = 4 + Math.floor(r() * 6);
      for (let j = 0; j < len; j++) {
        if (x >= 0 && x < S && y >= 0 && y < S) ctx.fillRect(x, y, 1, 1);
        x += dx; y += dy;
      }
    }
  }),

  mud: () => make(32, (ctx, S) => {
    base(ctx, S, '#5a4020');
    const r = rng(12);
    scatter(ctx, r, S, '#3a2810', 5, 2, 2);
    scatter(ctx, r, S, '#6a5028', 10);
    scatter(ctx, r, S, '#7a6038', 6);
    scatter(ctx, r, S, '#2a1808', 4, 1, 2);
    // Wet spots — darker 2×1 streaks
    ctx.fillStyle = '#281808';
    for (let i = 0; i < 3; i++) {
      ctx.fillRect(Math.floor(r() * (S - 2)), Math.floor(r() * S), 2, 1);
    }
  }),

  marble_floor: () => make(32, (ctx, S) => {
    base(ctx, S, '#d8d0c8');
    const r = rng(13);
    scatter(ctx, r, S, '#b8b0a8', 4, 2, 2);
    scatter(ctx, r, S, '#e8e0d8', 8);
    scatter(ctx, r, S, '#f0e8e0', 4);
    // Veins — straight diagonal 1-px lines
    ctx.fillStyle = '#9a9088';
    for (let i = 0; i < 2; i++) {
      let x = Math.floor(r() * S);
      let y = Math.floor(r() * S);
      const dx = r() > 0.5 ? 1 : -1;
      const len = 10 + Math.floor(r() * 10);
      for (let j = 0; j < len; j++) {
        if (x >= 0 && x < S && y >= 0 && y < S) ctx.fillRect(x, y, 1, 1);
        x += dx; y += 1;
      }
    }
  }),

  cave_rock: () => make(32, (ctx, S) => {
    // Warmer neutral gray so it reads differently from the cooler stone wall.
    base(ctx, S, '#3a3632');
    const r = rng(10);
    scatter(ctx, r, S, '#26221e', 6, 2, 2);
    scatter(ctx, r, S, '#4a453e', 10);
    scatter(ctx, r, S, '#5a554c', 6);
    scatter(ctx, r, S, '#100e0a', 4, 1, 2);
  }),

  cobblestone: () => make(32, (ctx, S) => {
    base(ctx, S, '#2a2a20'); // mortar
    const r = rng(14);
    // Fixed cobble layout; axis-aligned rects only
    const cobbles = [
      [1, 1, 13, 10], [15, 1, 16, 9],
      [1, 12, 10, 11], [12, 11, 10, 10], [23, 11, 8, 11],
      [1, 24, 13, 7], [15, 23, 16, 8],
    ];
    const palette = ['#6a6a58', '#5e5e4c', '#727260', '#646452', '#585848'];
    for (const [cx, cy, cw, ch] of cobbles) {
      ctx.fillStyle = palette[Math.floor(r() * palette.length)];
      ctx.fillRect(cx, cy, cw, ch);
      ctx.fillStyle = 'rgba(255,255,255,0.12)';
      ctx.fillRect(cx, cy, cw, 1);
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      ctx.fillRect(cx, cy + ch - 1, cw, 1);
      ctx.fillRect(cx + cw - 1, cy, 1, ch);
    }
  }),

  carpet: () => make(32, (ctx, S) => {
    base(ctx, S, '#6a1a3a');
    const r = rng(15);
    scatter(ctx, r, S, '#4a0a20', 8);
    scatter(ctx, r, S, '#8a2a50', 10);
    scatter(ctx, r, S, '#a0306a', 4);
    // Gold border (inset 1 px)
    ctx.fillStyle = '#a08040';
    ctx.fillRect(1, 1, S - 2, 1);
    ctx.fillRect(1, S - 2, S - 2, 1);
    ctx.fillRect(1, 1, 1, S - 2);
    ctx.fillRect(S - 2, 1, 1, S - 2);
    // Gold corner studs
    ctx.fillStyle = '#c0a050';
    ctx.fillRect(1, 1, 2, 2);
    ctx.fillRect(S - 3, 1, 2, 2);
    ctx.fillRect(1, S - 3, 2, 2);
    ctx.fillRect(S - 3, S - 3, 2, 2);
    // Center diamond accent
    ctx.fillStyle = '#c0a050';
    ctx.fillRect(S / 2 - 1, S / 2 - 2, 2, 1);
    ctx.fillRect(S / 2 - 2, S / 2 - 1, 4, 2);
    ctx.fillRect(S / 2 - 1, S / 2 + 1, 2, 1);
  }),

  gravel: () => make(32, (ctx, S) => {
    base(ctx, S, '#505044');
    const r = rng(16);
    // 2×2 pebbles in varied grays
    const palette = ['#787868', '#5a5a4a', '#7a7a6a', '#606050', '#848474', '#383828'];
    for (let i = 0; i < 14; i++) {
      ctx.fillStyle = palette[Math.floor(r() * palette.length)];
      const x = Math.floor(r() * (S - 2));
      const y = Math.floor(r() * (S - 2));
      ctx.fillRect(x, y, 2, 2);
      // 1-px highlight on top of each pebble
      ctx.fillStyle = 'rgba(255,255,255,0.18)';
      ctx.fillRect(x, y, 2, 1);
    }
    scatter(ctx, r, S, '#6a6a58', 6);
  }),

  swamp: () => make(32, (ctx, S) => {
    base(ctx, S, '#2a4020');
    const r = rng(17);
    scatter(ctx, r, S, '#142810', 5, 2, 2);
    scatter(ctx, r, S, '#3a5a20', 10);
    scatter(ctx, r, S, '#4a7030', 5);
    // Bubbles / muck spots
    scatter(ctx, r, S, '#1a3010', 3, 2, 2);
    scatter(ctx, r, S, '#5a8030', 4);
    // Reed stems — 1×2 verticals
    ctx.fillStyle = '#2a4010';
    for (let i = 0; i < 3; i++) {
      ctx.fillRect(Math.floor(r() * S), Math.floor(r() * (S - 3)), 1, 3);
    }
  }),

  bramble: () => make(32, (ctx, S) => {
    base(ctx, S, '#2a3a10');
    const r = rng(18);
    scatter(ctx, r, S, '#142008', 4, 2, 2);
    scatter(ctx, r, S, '#3a4a20', 8);
    // Twig diagonals
    ctx.fillStyle = '#5a4818';
    for (let i = 0; i < 4; i++) {
      let x = Math.floor(r() * S);
      let y = Math.floor(r() * S);
      const dx = r() > 0.5 ? 1 : -1;
      const dy = r() > 0.5 ? 1 : -1;
      const len = 5 + Math.floor(r() * 5);
      for (let j = 0; j < len; j++) {
        if (x >= 0 && x < S && y >= 0 && y < S) ctx.fillRect(x, y, 1, 1);
        x += dx; y += dy;
      }
    }
    // Thorn dots
    scatter(ctx, r, S, '#8a6820', 4);
    // Leaf dots
    scatter(ctx, r, S, '#4a6020', 5);
  }),

  obsidian: () => make(32, (ctx, S) => {
    // Stronger purple tint so it's clearly distinct from void (neutral black).
    base(ctx, S, '#14102a');
    const r = rng(19);
    scatter(ctx, r, S, '#0a081a', 4, 2, 2);
    scatter(ctx, r, S, '#5a40a0', 6);
    scatter(ctx, r, S, '#a080e0', 4);
    scatter(ctx, r, S, '#d0b0f0', 2);
    // Angular fracture lines
    ctx.fillStyle = '#20182a';
    for (let i = 0; i < 2; i++) {
      let x = Math.floor(r() * S);
      let y = Math.floor(r() * S);
      const dx = r() > 0.5 ? 1 : -1;
      const dy = r() > 0.5 ? 1 : -1;
      const len = 6 + Math.floor(r() * 8);
      for (let j = 0; j < len; j++) {
        if (x >= 0 && x < S && y >= 0 && y < S) ctx.fillRect(x, y, 1, 1);
        x += dx; y += dy;
      }
    }
  }),

  void: () => make(32, (ctx, S) => {
    base(ctx, S, '#06060c');
    const r = rng(20);
    scatter(ctx, r, S, '#02020a', 3, 2, 2);
    scatter(ctx, r, S, '#1a1428', 8);
    // Stars
    scatter(ctx, r, S, '#7870a0', 10);
    scatter(ctx, r, S, '#e0d8f0', 5);
    // Bright star with cross
    const sx = Math.floor(r() * (S - 2)) + 1;
    const sy = Math.floor(r() * (S - 2)) + 1;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(sx, sy, 1, 1);
    ctx.fillStyle = '#c0b0e0';
    ctx.fillRect(sx - 1, sy, 1, 1);
    ctx.fillRect(sx + 1, sy, 1, 1);
    ctx.fillRect(sx, sy - 1, 1, 1);
    ctx.fillRect(sx, sy + 1, 1, 1);
  }),

  // ── Wall types ─────────────────────────────────────────────────────────────

  wood_wall: () => make(32, (ctx, S) => {
    // Brighter than dark stone so wood reads clearly. Four planks, 8 px each,
    // each with one subtle grain streak — no noise.
    base(ctx, S, '#2a1808');
    const planks = ['#5a3620', '#4a2c18', '#623a22', '#502e1a'];
    const grainY = [9, 18, 5, 22];
    for (let col = 0; col < 4; col++) {
      const px = col * 8;
      ctx.fillStyle = planks[col];
      ctx.fillRect(px, 0, 7, S);
      // Single grain streak per plank
      ctx.fillStyle = '#2a1808';
      ctx.fillRect(px + 1, grainY[col], 5, 1);
      // Top highlight
      ctx.fillStyle = '#7a4a2a';
      ctx.fillRect(px, 0, 7, 1);
      // Dark divider at right edge of plank
      ctx.fillStyle = '#0a0402';
      ctx.fillRect(px + 7, 0, 1, S);
    }
  }),

  brick_wall: () => make(32, (ctx, S) => {
    base(ctx, S, '#2a1410'); // mortar
    const r = rng(22);
    const brickH = 8, brickW = 15, gap = 1;
    const palette = ['#7a3a2a', '#6a3020', '#823e30', '#703428'];
    for (let row = 0; row < 4; row++) {
      const offset = (row % 2) * Math.floor((brickW + gap) / 2);
      for (let col = -1; col < 4; col++) {
        const bx = col * (brickW + gap) + offset;
        const by = row * (brickH + gap);
        ctx.fillStyle = palette[Math.floor(r() * palette.length)];
        ctx.fillRect(bx, by, brickW, brickH);
        // Highlight top, shadow bottom
        ctx.fillStyle = '#a0584a';
        ctx.fillRect(bx, by, brickW, 1);
        ctx.fillStyle = '#401a10';
        ctx.fillRect(bx, by + brickH - 1, brickW, 1);
      }
    }
  }),

  hedge: () => make(32, (ctx, S) => {
    base(ctx, S, '#0e2808');
    const r = rng(23);
    const leaf = ['#1a4a10', '#124008', '#206018', '#183a0c'];
    // Fill densely with 2×2 leaf clusters
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = leaf[Math.floor(r() * leaf.length)];
      ctx.fillRect(Math.floor(r() * (S - 2)), Math.floor(r() * (S - 2)), 2, 2);
    }
    // Highlight speckles
    scatter(ctx, r, S, '#308028', 10);
    // Dark shadow pits
    scatter(ctx, r, S, '#061404', 8);
  }),

  iron_bars: () => make(32, (ctx, S) => {
    base(ctx, S, '#14141e');
    const r = rng(24);
    const barW = 3, step = 8;
    for (let col = 0; col < 5; col++) {
      const bx = col * step - 1;
      // Bar body
      ctx.fillStyle = '#3a3a4a';
      ctx.fillRect(bx, 0, barW, S);
      // Left highlight, right shadow
      ctx.fillStyle = '#6a6a80';
      ctx.fillRect(bx, 0, 1, S);
      ctx.fillStyle = '#0a0a14';
      ctx.fillRect(bx + barW - 1, 0, 1, S);
    }
    // Horizontal cross-bars
    const rows = [7, 23];
    for (const cy of rows) {
      ctx.fillStyle = '#3a3a4a';
      ctx.fillRect(0, cy, S, 3);
      ctx.fillStyle = '#6a6a80';
      ctx.fillRect(0, cy, S, 1);
      ctx.fillStyle = '#0a0a14';
      ctx.fillRect(0, cy + 2, S, 1);
    }
    // Rust flecks
    scatter(ctx, r, S, '#5a2a10', 5);
  }),

};

// ── Public API ─────────────────────────────────────────────────────────────

export function getTexture(terrainId) {
  if (!GENERATORS[terrainId]) return null;
  if (!CACHE[terrainId]) CACHE[terrainId] = GENERATORS[terrainId]();
  return CACHE[terrainId];
}
